export type FixedPanelId = 'probability' | 'clock' | 'flow' | 'position';

export type OwnPanelId = `own:${string}`;

export type PanelId = FixedPanelId | OwnPanelId;

export type Unit =
    | 'probability'
    | 'usd'
    | 'shares'
    | 'price'
    | 'budget'
    | 'rate'
    | 'bps'
    | 'clock';

export type DrawKind =
    | 'line'
    | 'step'
    | 'area'
    | 'candles'
    | 'bars'
    | 'markers';

export type AxisMode = 'shared' | 'own' | 'hidden';

export type AxisSide = 'left' | 'right';

export interface SeriesPoint {
    t: number;
    v: number;
}

export interface SeriesCandle {
    t: number;
    open: number;
    high: number;
    low: number;
    close: number;
}

export type MarkerShape = 'circle' | 'triangle' | 'diamond' | 'up' | 'down';

export interface SeriesMarker {
    id: string;
    t: number;
    v: number | null;
    color: string;
    shape: MarkerShape;
    title: string;
    detail: string | null;
    weight?: number;
}

export type SeriesData =
    | { kind: 'points'; points: SeriesPoint[] }
    | { kind: 'candles'; candles: SeriesCandle[] }
    | { kind: 'markers'; markers: SeriesMarker[] };

export interface ChartSeries {
    id: string;
    label: string;
    /** The legend text in the home panel, when `label` is too long for it. */
    legendLabel?: string;
    /**
     * Short legend text for when the series is drawn outside its home panel.
     * It is then listed in a row of its own, without its value (the cursor
     * tooltip has it), and `label` stays as its name in the tooltips.
     */
    awayLabel?: string;
    color: string;
    unit: Unit;
    draw: DrawKind;
    axis: AxisMode;
    home: FixedPanelId;
    movable: boolean;
    hasData: boolean;
    dashed?: boolean;
    /**
     * A dash pattern (stroke-dasharray) for when the series is drawn outside
     * its home panel, where it would otherwise look like a line that lives
     * there. It replaces `dashed` away from home and never applies at home.
     */
    awayDash?: string;
    faint?: boolean;
    /** The last point is a streamed price, not a candle. */
    live?: boolean;
    image?: string | null;
    data: SeriesData;
}

export interface PanelAxis {
    key: string;
    unit: Unit;
    side: AxisSide;
    seriesIds: string[];
}

export interface PanelPlan {
    id: PanelId;
    series: ChartSeries[];
    axes: PanelAxis[];
}

export type SeriesMoves = Readonly<Record<string, PanelId>>;

export function ownPanelId(seriesId: string): OwnPanelId {
    return `own:${seriesId}`;
}

export function isOwnPanel(id: PanelId): id is OwnPanelId {
    return id.startsWith('own:');
}
